import { resendInvitationProvider } from '@/apis/Providers/requests.ts'
import { LogUserActivity } from '@/app/apis/users/requests'
import { Button } from '@/app/common/components/button/Button'
import { LOG_TYPES } from '@/app/common/constants'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import MultipleSelector from '@/common/components/MultipleSelector.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import ProviderBankAccountStatusBadge from '@/common/components/ProviderBankAccountStatusBadge'
import SearchInput from '@/common/components/SearchInput.tsx'
import ApplicationStatusBadge from '@/common/components/StatusBadge'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { CircleArrowsIcon } from '@/common/icons/arrows.tsx'
import { DownIcon, FilterIcon, UpIcon } from '@/common/icons/general.tsx'
import { cn, getSemanticColor } from '@/common/utilities.ts'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { formatDateToUsersTimezone } from '@/lib/utils'
import ProviderImportModal from '@/pages/providers/bulk_upload_providers/View.tsx'
import InviteProvider from '@/pages/providers/Invite/InviteProvider.tsx'
import { ProviderQueries } from '@/queries/providers/queries'
import { ProviderList, type ProviderSortBy } from '@/queries/providers/requests'
import { useExportProviders } from '@/queries/providers/useExportProviders.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { useToast } from '@/shad/ui/toast/use-toast'
import { Provider } from '@/schemas/provider.schema'
import { RootState } from '@/storage/store'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useDebounce } from '@uidotdev/usehooks'
import { format } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import AdminAuthGuard from './admin-auth-guard'
import Badge from '@/common/components/Badge'

type ListItem = {
  id: string | number
  name: string
}

const TABLE_FILTERS_STATUS: ListItem[] = [
  { id: 1, name: 'Invitation Sent' },
  { id: 2, name: 'Application In Progress' },
  { id: 3, name: 'Application Submitted' },
  { id: 4, name: 'Application Accepted' },
  { id: 5, name: 'Application Rejected' },
]

type ProviderSortableColumnLabel =
  | 'ID'
  | 'Status'
  | 'Name'
  | 'Category'
  | 'State'
  | 'Application'
  | 'Banking Information'
  | 'Submitted At'

const columnLabelValDic: Record<ProviderSortableColumnLabel, ProviderSortBy> = {
  ID: 'id',
  Status: 'provider_status',
  Name: 'name',
  Category: 'category',
  State: 'state_name',
  Application: 'status',
  'Banking Information': 'bank_account_status',
  'Submitted At': 'submitted_at',
}

const columnHelper = createColumnHelper<Provider>()
const RECORDS_PER_PAGE = 10 as const
const THREE_MINUTES = 1000 * 60 * 3
const InitialProviders: ProviderList = []

const COL_WIDTHS: Partial<Record<string, string>> = {
  id: 'w-20',
  provider_status: 'w-24',
}

function headerForColumn(
  name: ProviderSortableColumnLabel,
  sort_by: ProviderSortBy,
  sort_order: 'asc' | 'desc',
  onSort: (column: ProviderSortableColumnLabel) => void,
  className = ''
) {
  const colVar = columnLabelValDic[name]
  const isActiveSort = colVar === sort_by

  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1 bg-transparent p-0 text-left',
        className
      )}
      onClick={() => onSort(name)}
      aria-label={`Sort by ${name}`}
    >
      {name}
      <span className="inline-flex flex-col items-center leading-none">
        <UpIcon
          className={cn(
            'text-xs leading-none',
            isActiveSort && sort_order === 'asc'
              ? 'text-gray-900'
              : 'text-gray-400'
          )}
        />
        <DownIcon
          className={cn(
            '-mt-1 text-xs leading-none',
            isActiveSort && sort_order === 'desc'
              ? 'text-gray-900'
              : 'text-gray-400'
          )}
        />
      </span>
    </button>
  )
}

export default function ProvidersPage() {
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setsearch] = useState('')
  const [inviteModalVisible, setInviteModalVisible] = useState(false)
  const [importModalVisible, setImportModalVisible] = useState(false)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)
  const [selectedFilter, setSelectedFilter] = useState<ListItem[]>([])
  const [sort_by, setSortBy] = useState<ProviderSortBy>('id')
  const [sort_order, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const { fireAlert } = useAppAlert()
  const { toast } = useToast()

  const navigate = useNavigate()
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const debouncedTerm = useDebounce(search, 500)

  const statusInFilter = selectedFilter.map((f) => f.id).join(',')

  const ProviderListQuery = useQuery(
    ProviderQueries.getPaginatedProviders(
      selectedProgramYear?.id ?? 0,
      recordsPerPage,
      currentPage,
      debouncedTerm,
      sort_by,
      sort_order,
      statusInFilter,
      {
        enabled: true,
        placeholderData: keepPreviousData,
        refetchInterval: THREE_MINUTES,
      }
    )
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [search, selectedFilter])

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Provider page',
      })
      hasLoggedPageView.current = true
    }
  }, [])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
  }

  const handleResendInvitation = async (id: number) => {
    const payload = {
      provider_id: id,
      program_id: selectedProgramYear?.id ?? 0,
    }

    const globalOnId = 1
    const response = await resendInvitationProvider(payload, globalOnId)

    if (response.error) {
      const errorMessage =
        response.error.message ||
        'An unexpected error occurred. Please try again later.'

      await fireAlert({
        variant: 'error',
        message: errorMessage,
      })
      return
    }

    await fireAlert({
      variant: 'success',
      message: response.data?.message || 'Invitation resent successfully!',
    })

    refetchProviderList()
  }
  const sortBy = (column: ProviderSortableColumnLabel) => {
    const colVar = columnLabelValDic[column]
    const newOrder =
      colVar === sort_by ? (sort_order === 'desc' ? 'asc' : 'desc') : 'asc'

    setCurrentPage(1)
    setSortBy(colVar)
    setSortOrder(newOrder)
  }

  const exportProvidersMutation = useExportProviders({
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'providers.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => window.URL.revokeObjectURL(url), 0)

      toast({
        title: 'Providers list has been exported successfully.',
        variant: 'default',
      })
    },
    onError: (error) => {
      toast({
        title: 'Failed to export providers',
        description: error.message || 'Unable to export providers.',
        variant: 'destructive',
      })
    },
  })

  const handleExportProviders = () => {
    if (!selectedProgramYear?.id || exportProvidersMutation.isPending) {
      return
    }

    const trimmedSearch = search.trim()

    exportProvidersMutation.mutate({
      programId: selectedProgramYear.id,
      ...(trimmedSearch && { search: trimmedSearch }),
      ...(statusInFilter && { statusIn: statusInFilter }),
    })
  }

  const columns = [
    columnHelper.accessor('id', {
      id: 'id',
      header: () => headerForColumn('ID', sort_by, sort_order, sortBy, 'pl-4'),
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="w-3/4 sm:w-full font-bold pl-4">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <span>{row.id}</span>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('provider_status', {
      id: 'provider_status',
      header: () => headerForColumn('Status', sort_by, sort_order, sortBy),
      cell: (info) => {
        const status = info.getValue()

        return <Badge semanticColor={getSemanticColor(status)}>{status}</Badge>
      },
    }),
    columnHelper.accessor('name', {
      id: 'name',
      header: () =>
        headerForColumn('Name', sort_by, sort_order, sortBy, 'pl-4'),
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="w-3/4 sm:w-full font-bold pl-4">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <span>{row.name}</span>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('provider_application.provider_categories', {
      id: 'category',
      header: () => headerForColumn('Category', sort_by, sort_order, sortBy),
      cell: (info) => {
        const row = info.row.original
        const categories = row.provider_application?.provider_categories
          ?.map((category) => category.global_provider_category?.name)
          .filter(Boolean)
          .join(', ')
        return <>{categories}</>
      },
    }),
    columnHelper.accessor('state_name', {
      id: 'state_name',
      header: () => headerForColumn('State', sort_by, sort_order, sortBy),
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="font-medium text-gray-900 flex justify-between items-center">
              <span>{row.state_name}</span>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('provider_application.status', {
      id: 'status',
      header: () =>
        headerForColumn(
          'Application',
          sort_by,
          sort_order,
          sortBy,
          'pl-4 w-44 whitespace-nowrap'
        ),
      cell: (info) => {
        const row = info.row.original
        const status = row.provider_application?.status ?? ''
        return <ApplicationStatusBadge status={status} />
      },
    }),
    columnHelper.accessor('bank_account_status', {
      id: 'bank_account_status',
      header: () =>
        headerForColumn(
          'Banking Information',
          sort_by,
          sort_order,
          sortBy,
          'w-full justify-center whitespace-nowrap'
        ),
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex w-full justify-center">
            <ProviderBankAccountStatusBadge status={row.bank_account_status} />
          </div>
        )
      },
    }),
    columnHelper.display({
      id: 'submitted_at',
      header: () =>
        headerForColumn(
          'Submitted At',
          sort_by,
          sort_order,
          sortBy,
          'pl-4 w-44 whitespace-nowrap'
        ),
      cell: (info) => {
        const row = info.row.original
        const submitted_at = row.provider_application?.submitted_at ?? ''
        return (
          <span>
            {submitted_at ? formatDateToUsersTimezone(submitted_at) : ''}
          </span>
        )
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => (
        <span className="inline-block w-20 whitespace-nowrap text-right pr-2">
          Action
        </span>
      ),
      cell: (info) => {
        const row = info.row.original
        const status = row.provider_application?.status
        return (
          <>
            {status === 'Invited' ? (
              <Button
                size="xs"
                className="w-32 text-sm font-medium truncate"
                onClick={() => {
                  handleResendInvitation(row.id)
                }}
              >
                Resend Invitation
              </Button>
            ) : (
              <Button
                size="xs"
                className="w-32 text-sm font-medium truncate"
                onClick={() => {
                  navigate(
                    AppRoutePaths.providers.provider_details.provider_info.replace(
                      ':providerId',
                      row.id.toString()
                    )
                  )
                }}
              >
                Review
              </Button>
            )}
          </>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: ProviderListQuery.data?.data ?? InitialProviders,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: ProviderListQuery.data?.last_page,
  })

  const refetchProviderList = () =>
    ProviderListQuery.refetch({ throwOnError: true, cancelRefetch: false })

  return (
    <>
      <div className="flex ml-auto gap-3">
        <AdminAuthGuard requiredPermissions={['provider_management_create']}>
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              setImportModalVisible((prev) => !prev)
            }}
          >
            Upload Providers
          </Button>

          <Button
            size="lg"
            onClick={() => {
              setInviteModalVisible((prev) => !prev)
            }}
          >
            Invite Provider
          </Button>
        </AdminAuthGuard>
      </div>
      <section aria-labelledby="providers-list-title">
        <Card className="mt-6">
          <CardHeader className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-md font-semibold gap-4 sm:gap-0">
            <div className="text-left text-lg md:text-md text-gray-900 w-full">
              Providers
              <span className="hidden mt-1 w-15ch text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {ProviderListQuery.isFetching
                  ? 'refreshing data...'
                  : 'last updated at: ' +
                    format(ProviderListQuery.dataUpdatedAt, 'hh:mm:ss a')}{' '}
              </span>
            </div>
            <div className="w-full sm:w-auto">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 w-full gap-3">
                <MultipleSelector
                  className="w-full sm:w-[220px] lg:w-[250px]"
                  list={TABLE_FILTERS_STATUS.sort((a, b) =>
                    a.name.localeCompare(b.name)
                  )}
                  selected={selectedFilter}
                  setSelected={setSelectedFilter}
                  icon={<FilterIcon className="text-primary" />}
                />
                <SearchInput
                  className="w-full sm:w-[200px] lg:w-[250px]"
                  id="table_search"
                  placeholder="Search Providers"
                  value={search}
                  onChange={(e) => setsearch(e.target.value)}
                />
                <SubmitButton
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-32 whitespace-nowrap"
                  status={exportProvidersMutation.status}
                  buttonTxt="Export"
                  submittingText="Exporting..."
                  forceDisabled={!selectedProgramYear?.id}
                  onClick={handleExportProviders}
                />
                <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                  <Button
                    variant="outline"
                    className="max-w-12"
                    size="md"
                    disabled={ProviderListQuery.isFetching}
                    onClick={refetchProviderList}
                  >
                    <CircleArrowsIcon className="text-base" />
                  </Button>
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {ProviderListQuery.isFetching
                      ? 'refreshing data...'
                      : 'last updated at: ' +
                        format(ProviderListQuery.dataUpdatedAt, 'hh:mm:ss a')}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="w-full p-0">
            <div className="overflow-auto w-full h-[55dvh]">
              <table className="w-full table-auto">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={cn(
                            'px-2 py-3.5 text-left text-sm font-semibold text-gray-900 whitespace-nowrap',
                            COL_WIDTHS[header.column.id] ??
                              'min-w-[140px] max-w-[220px]'
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            'py-4 px-2 text-sm text-left text-gray-500 border-t border-gray-200 truncate whitespace-nowrap',
                            COL_WIDTHS[cell.column.id] ??
                              'min-w-[140px] max-w-[220px]'
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
          <CardFooter className="p-0 border-t border-transparent mt-5">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPage}
              totalRows={ProviderListQuery.data?.total ?? 0}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>

          <InviteProvider
            inviteModalVisible={inviteModalVisible}
            setInviteModalVisible={setInviteModalVisible}
            selectedProgramYear={selectedProgramYear?.id ?? 0}
            inviteIsFetching={false}
          />

          <ProviderImportModal
            importModalVisible={importModalVisible}
            setImportModalVisible={setImportModalVisible}
          />
        </Card>
      </section>
    </>
  )
}
